<template>
  <div class="bg-white rounded-lg border border-growth-50 overflow-hidden">
    <div class="px-4 py-3 border-b border-growth-50 flex justify-between items-center">
      <h3 class="text-sm font-semibold text-growth-900">Providers ({{ total.toLocaleString() }})</h3>
      <select v-model="sort" @change="reload" class="text-xs border border-growth-50 rounded px-2 py-1">
        <option value="volume">Sort: Volume</option>
        <option value="medicare">Sort: Medicare $</option>
        <option value="medicaid">Sort: Medicaid $</option>
        <option value="name">Sort: Name</option>
        <option value="specialty">Sort: Specialty</option>
      </select>
    </div>

    <div v-if="loading && !providers.length" class="p-6 text-center text-growth-600">
      <svg class="animate-spin h-5 w-5 mx-auto mb-2" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
      Loading providers...
    </div>

    <template v-else-if="providers.length">
      <div class="px-4 pb-4">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-growth-600 uppercase border-b border-growth-50">
              <th class="pb-2 pr-4"></th>
              <th class="pb-2 pr-4">Name</th>
              <th class="pb-2 pr-4">Specialty</th>
              <th class="pb-2 pr-4 text-right">Benes</th>
              <th class="pb-2 pr-4 text-right">Medicare $</th>
              <th class="pb-2 text-right">Medicaid $</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in providers" :key="p.NPI" class="border-b border-growth-50 hover:bg-growth-10 transition-colors">
              <td class="py-1.5 pr-2"><span :class="p.BENEFICIARIES >= 500 ? 'text-emerald-500' : p.BENEFICIARIES ? 'text-growth-info' : 'text-growth-50'">●</span></td>
              <td class="py-1.5 pr-4 font-medium text-growth-900">{{ p.PROVIDER_NAME }}{{ p.CREDENTIALS ? ', ' + p.CREDENTIALS : '' }}</td>
              <td class="py-1.5 pr-4 text-growth-600">{{ p.SPECIALTY || '—' }}</td>
              <td class="py-1.5 pr-4 text-right tabular-nums">{{ p.BENEFICIARIES ? p.BENEFICIARIES.toLocaleString() : '—' }}</td>
              <td class="py-1.5 pr-4 text-right tabular-nums">{{ p.MEDICARE_PAYMENT ? '$' + Math.round(p.MEDICARE_PAYMENT).toLocaleString() : '—' }}</td>
              <td class="py-1.5 text-right tabular-nums">{{ p.MEDICAID_PAYMENT ? '$' + Math.round(p.MEDICAID_PAYMENT).toLocaleString() : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="border-t border-growth-50 px-4 py-2 flex justify-between items-center text-xs text-growth-600">
        <span>{{ offset + 1 }}–{{ Math.min(offset + providers.length, total) }} of {{ total.toLocaleString() }}</span>
        <div class="flex gap-1">
          <button @click="prevPage" :disabled="offset === 0" class="px-3 py-1 border border-growth-50 rounded disabled:opacity-50 hover:bg-growth-10 transition-colors">Prev</button>
          <button @click="nextPage" :disabled="offset + limit >= total" class="px-3 py-1 border border-growth-50 rounded disabled:opacity-50 hover:bg-growth-10 transition-colors">Next</button>
        </div>
      </div>
    </template>

    <div v-else class="p-6 text-center text-sm text-growth-600">No providers found</div>
  </div>
</template>

<script>
export default {
  name: 'PracticeProviders',
  props: {
    pacId: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      providers: [],
      total: 0,
      limit: 10,
      offset: 0,
      sort: 'volume',
    }
  },
  watch: {
    pacId: {
      immediate: true,
      handler(id) {
        if (id) this.reload()
        else { this.providers = []; this.total = 0 }
      },
    },
  },
  methods: {
    async reload() {
      this.offset = 0
      await this.fetchProviders()
    },
    async fetchProviders() {
      this.loading = true
      try {
        const response = await this.$axios.get(`/api/growth/practices/${this.pacId}/providers/`, {
          params: { limit: this.limit, offset: this.offset, sort: this.sort },
        })
        this.providers = response.data.providers || []
        this.total = response.data.total || 0
      } catch (err) {
        console.error('Failed to load providers', err)
        this.providers = []
      } finally {
        this.loading = false
      }
    },
    prevPage() {
      if (this.offset > 0) {
        this.offset -= this.limit
        this.fetchProviders()
      }
    },
    nextPage() {
      if (this.offset + this.limit < this.total) {
        this.offset += this.limit
        this.fetchProviders()
      }
    },
  },
}
</script>
